import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

const parAt = (item, branch) => {
  const pv = item.par_values || {};
  return pv[branch.branch_id] || pv[branch.name] || 0;
};

export const AddItemsDialog = ({ open, onOpenChange, items, branches, onAdd, saving }) => {
  const [branchId, setBranchId] = useState('');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState({});

  useEffect(() => {
    if (open) { setPicked({}); setSearch(''); setBranchId(branches[0]?.branch_id || ''); }
  }, [open, branches]);

  const branch = branches.find((b) => b.branch_id === branchId);
  const available = useMemo(() => (branch ? items.filter((it) => !(parAt(it, branch) > 0)
    && (!search || it.name.toLowerCase().includes(search.toLowerCase()))) : []), [items, branch, search]);

  const toggle = (id, on) => setPicked((p) => {
    const n = { ...p };
    if (on) n[id] = n[id] ?? ''; else delete n[id];
    return n;
  });

  const ids = Object.keys(picked);
  const valid = ids.length > 0 && ids.every((id) => parseFloat(picked[id]) > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="par-add-dialog">
        <DialogHeader>
          <DialogTitle>Add items to a branch</DialogTitle>
          <DialogDescription>Tick existing stock items and give each a PAR. They will then show in that branch's stock take.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-3">
          {branches.length > 1 ? (
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); setPicked({}); }}>
              <SelectTrigger className="w-52" data-testid="par-add-branch-select"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          ) : <p className="text-sm font-medium self-center">{branch?.name}</p>}
          <Input placeholder="Search items" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="par-add-search" />
        </div>
        <div className="flex-1 overflow-y-auto border rounded-md divide-y min-h-[200px]">
          {available.map((it) => {
            const on = picked[it.stock_item_id] !== undefined;
            return (
              <div key={it.stock_item_id} className="flex items-center gap-3 px-3 py-2" data-testid={`par-add-row-${it.stock_item_id}`}>
                <Checkbox checked={on} onCheckedChange={(v) => toggle(it.stock_item_id, !!v)} data-testid={`par-add-check-${it.stock_item_id}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium">{it.name}</p>
                  <p className="text-xs text-muted-foreground">{it.supplier_name || '—'} · {it.unit}</p>
                </div>
                {on && (
                  <Input type="number" min="0" step="0.01" placeholder="PAR" className="w-24 h-8" autoFocus
                    value={picked[it.stock_item_id]} onChange={(e) => setPicked((p) => ({ ...p, [it.stock_item_id]: e.target.value }))}
                    data-testid={`par-add-input-${it.stock_item_id}`} />
                )}
              </div>
            );
          })}
          {available.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">All items already have a PAR at this branch.</p>}
        </div>
        <DialogFooter>
          <span className="text-sm text-muted-foreground mr-auto self-center">{ids.length} selected</span>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!valid || saving} data-testid="par-add-submit-btn"
            onClick={() => onAdd(ids.map((id) => ({ stock_item_id: id, branch_id: branchId, par: parseFloat(picked[id]) })))}>
            {saving ? 'Adding...' : `Add ${ids.length || ''} item${ids.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
